import type { ReactNode } from 'react';
import { CheckIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { CARD } from '@/components/ui';
import { cn } from '@/lib/utils';

/**
 * 渐进式步骤卡：序号圆点（进行中=实心，已完成=对勾，未激活=灰+降透明度）。
 * 上一步完成才渲染下一步，形成「一步一步」的观感。
 *
 * 对齐搬小书 `apps/web/src/app/mobile/tools/step-card.tsx`。
 */
export function StepCard({
  number,
  title,
  subtitle,
  state = 'active',
  children,
}: {
  number: number;
  title: string;
  subtitle?: string;
  state?: 'inactive' | 'active' | 'complete';
  children: ReactNode;
}) {
  const badge =
    state === 'complete'
      ? 'bg-primary/12 text-primary'
      : state === 'active'
        ? 'bg-primary text-primary-foreground'
        : 'bg-muted text-muted-foreground';

  return (
    <Card className={cn(CARD, 'gap-3', state === 'inactive' && 'opacity-55')}>
      <div className="flex items-start gap-3">
        <span
          className={cn(
            'flex size-7 shrink-0 items-center justify-center rounded-full text-caption1 font-bold',
            // 原生里没有网页式的序号圆点：iOS 下只留标题，靠卡片出现的顺序表达步骤
            'ios:hidden',
            badge,
          )}
        >
          {state === 'complete' ? <CheckIcon className="size-3.5" strokeWidth={3} /> : number}
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-callout leading-tight font-semibold ios:text-body">{title}</h2>
          {subtitle ? <p className="text-muted-foreground mt-0.5 text-footnote ios:text-subheadline">{subtitle}</p> : null}
        </div>
      </div>
      <div>{children}</div>
    </Card>
  );
}
